import React, { JSX } from "react";
import { Box, TextField, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import startCase from "lodash/startCase";
import DeleteIcon from "@mui/icons-material/Delete";
import produce from "immer";
import { BillingPackage, BillingPackageItem } from "../../../interfaces/BillingPackages";
import {
  getTotalPayingAmount,
  ITEM_TYPES,
  removeBillItem,
  updateDiscountForItem,
  updateDiscountForItemInRupee,
  updatePayingAmountForItem
} from "../../../helpers/billingPackages";
import SummaryItemRow from "./SummaryItemRow";
import { BILLING_SOURCES } from "../../Reports/Filters";
import { ProductType } from "../../../interfaces/ProductInterface";

const IndividualRow = React.memo(
  ({
    item,
    setFormData,
    index,
    items,
    itemType,
    setTotalPayingAmount,
    isSubItem
  }: {
    item: BillingPackageItem;
    items: BillingPackage;
    index: number;
    itemType: ITEM_TYPES;
    setFormData: (v: BillingPackage) => void;
    setTotalPayingAmount: (v: number) => void;
    isSubItem?: boolean;
  }): JSX.Element => {
    const theme = useTheme();
    return (
      <>
        {index === 0 && itemType && (
          <TableRow
            sx={{
              backgroundColor: theme.palette.action.hover
            }}
          >
            <TableCell colSpan={12} sx={{ textAlign: "center" }}>
              <Typography variant="h6">{startCase(itemType)}</Typography>
            </TableCell>
          </TableRow>
        )}
        <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
          <TableCell sx={{ pl: isSubItem ? 4 : undefined }}>{item.description}</TableCell>
          <TableCell>{item.note}</TableCell>
          <TableCell>{(item.teeth || []).join(", ")}</TableCell>
          <TableCell>
            <TextField
              value={item.quantity}
              label="Qty"
              disabled={item.source !== BILLING_SOURCES.STOCKS || Number(item.paidAmount || 0) > 0}
              placeholder="qty"
              size="small"
              sx={{ width: 50 }}
              onChange={(event) => {
                const qty = Number(event.target.value) < 1 ? 1 : Number(event.target.value);

                const updatedData = produce(items, (draft) => {
                  draft.document.nonPackaged[index] = {
                    ...item,
                    quantity: qty,

                    netAmount: qty * item.perUnit,
                    grossTotal: qty * item.perUnit,
                    payingAmount: qty * item.perUnit
                  };
                });
                setTotalPayingAmount(getTotalPayingAmount(updatedData.document));
                setFormData(updatedData);
              }}
              variant="outlined"
            />
          </TableCell>
          <TableCell>
            <TextField
              value={item.perUnit}
              placeholder="per unit"
              label="Price"
              sx={{ width: 100, textAlign: "right" }}
              disabled={
                item.source === BILLING_SOURCES.STOCKS ||
                item.category === ProductType.labTest ||
                Number(item.paidAmount || 0) > 0
              }
              size="small"
              onChange={(event) => {
                const newPrice = Number(event.target.value || 0);
                const updatedData = produce(items, (draft) => {
                  draft.document.nonPackaged[index] = {
                    ...item,
                    perUnit: newPrice,
                    netAmount: newPrice * item.quantity,
                    grossTotal: newPrice * item.quantity,
                    payingAmount: newPrice * item.quantity
                  };
                });
                setTotalPayingAmount(getTotalPayingAmount(updatedData.document));
                setFormData(updatedData);
              }}
              variant="outlined"
            />
          </TableCell>
          <TableCell align="right">{Number(item.dueAmount || 0).toFixed(2)}</TableCell>
          <TableCell align="right">{Number(item.paidAmount || 0).toFixed(2)}</TableCell>
          <TableCell>
            <TextField
              value={item.discountPercent}
              placeholder="percent"
              label="Dis %"
              sx={{ width: 80 }}
              disabled={
                item.source === BILLING_SOURCES.STOCKS ||
                item.category === ProductType.labTest ||
                Number(item.paidAmount || 0) > 0
              }
              slotProps={{
                inputLabel: { shrink: true }
              }}
              size="small"
              onChange={(event) => {
                const disPct = Number(event.target.value || 0);
                if (disPct >= 0 && disPct < 101) {
                  const updatedData = updateDiscountForItem(items, itemType, index, disPct);
                  setFormData(updatedData);
                  setTotalPayingAmount(getTotalPayingAmount(updatedData.document));
                }
              }}
              variant="outlined"
            />
          </TableCell>
          <TableCell align="right">
            <TextField
              value={item.discountAmt}
              slotProps={{
                inputLabel: { shrink: true }
              }}
              placeholder="amount"
              label="Dis amt"
              sx={{ width: 80 }}
              disabled={
                item.source === BILLING_SOURCES.STOCKS ||
                item.category === ProductType.labTest ||
                Number(item.paidAmount || 0) > 0
              }
              size="small"
              onChange={(event) => {
                const disAmt = Number(event.target.value || 0);
                if (disAmt >= 0 && disAmt < item.grossTotal) {
                  const updatedData = updateDiscountForItemInRupee(items, itemType, index, disAmt);
                  setFormData(updatedData);
                  setTotalPayingAmount(getTotalPayingAmount(updatedData.document));
                }
              }}
              variant="outlined"
            />
          </TableCell>
          <TableCell align="right">{Number(item.netAmount || 0).toFixed(2)}</TableCell>
          <TableCell align="right">
            <TextField
              value={
                itemType === ITEM_TYPES.PACKAGED
                  ? Number(item.payingAmount || 0)
                  : Number(item.netAmount || 0)
              }
              sx={{ width: 80, textAlign: "right" }}
              placeholder="Amount"
              size="small"
              onChange={(event) => {
                if (itemType !== ITEM_TYPES.PACKAGED) return;
                const due = Number(item.netAmount || 0) - Number(item.paidAmount || 0);

                if (Number(event.target.value || 0) > due) {
                  const updatedDoc = updatePayingAmountForItem(items, itemType, index, due);
                  setFormData(updatedDoc);
                  setTotalPayingAmount(getTotalPayingAmount(updatedDoc.document));
                } else {
                  const updatedDoc = updatePayingAmountForItem(
                    items,
                    itemType,
                    index,
                    Number(event.target.value)
                  );
                  setFormData(updatedDoc);

                  setTotalPayingAmount(getTotalPayingAmount(updatedDoc.document));
                }
              }}
              variant="outlined"
            />
          </TableCell>
          <TableCell>
            {item.source === BILLING_SOURCES.STOCKS &&
              Number(item.paidAmount) - Number(item.netAmount) !== 0 && (
                <DeleteIcon
                  sx={{ cursor: "pointer" }}
                  fontSize="small"
                  color="error"
                  onClick={() => {
                    const updatedData = removeBillItem({
                      section: itemType,
                      data: items,
                      itemIndex: index
                    });
                    setTotalPayingAmount(getTotalPayingAmount(updatedData.document));
                    setFormData(updatedData);
                  }}
                />
              )}
          </TableCell>
        </TableRow>
        {index === items.document[itemType].length - 1 && (
          <TableRow sx={{ "& td": { border: 0, p: 2 } }}>
            <TableCell colSpan={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  flexDirection: "column",
                  alignItems: "flex-end"
                }}
              >
                <SummaryItemRow
                  label="Due Amount"
                  value={items.document[itemType]
                    ?.reduce((prev, cur) => prev + Number(cur.dueAmount || 0), 0)
                    .toFixed(2)}
                />
                <SummaryItemRow
                  label="Paid Amount"
                  value={items.document[itemType]
                    ?.reduce((prev, cur) => prev + Number(cur.paidAmount || 0), 0)
                    .toFixed(2)}
                />
                <SummaryItemRow
                  label="Gross Total"
                  value={items.document[itemType]
                    ?.reduce((prev, cur) => prev + Number(cur.grossTotal || 0), 0)
                    .toFixed(2)}
                />
                {itemType === ITEM_TYPES.PACKAGED && <SummaryItemRow label="Discount" value="" />}
                <SummaryItemRow
                  label="Category Total Amount"
                  value={items.document[itemType]
                    ?.reduce((prev, cur) => prev + Number(cur.grossTotal || 0), 0)
                    .toFixed(2)}
                />
                <SummaryItemRow
                  label="Paying Amount"
                  value={items.document[itemType]
                    ?.reduce((prev, cur) => prev + Number(cur.payingAmount || 0), 0)
                    .toFixed(2)}
                />
              </Box>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }
);

export default IndividualRow;
